import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take, catchError } from 'rxjs/operators';
import clone from 'lodash-es/clone';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { ReportLayout, ReportTypeQueryParamValues } from './reports.types';
import { PaginatedResource } from '../../shared/models/paginated-resource';
import { GraphqlConnection } from '@shared/models/graphql';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private _reportLayout: BehaviorSubject<ReportLayout<any> | null>;
  private _reportLayouts: BehaviorSubject<ReportLayout<any>[] | null>;
  private _accountId: string;

  constructor(private _httpClient: HttpClient) {
    this._reportLayout = new BehaviorSubject(null);
    this._reportLayouts = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for contact
   */
  get reportLayout$(): Observable<ReportLayout<any>> {
    return this._reportLayout.asObservable();
  }

  /**
   * Getter for contacts
   */
  get reportLayouts$(): Observable<ReportLayout<any>[]> {
    return this._reportLayouts.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  /**
   * Get contacts
   */
  getReportLayouts(): Observable<ReportLayout<any>[] | null> {
    return this._httpClient
      .get<any>(`report_layouts?report_types=${ReportTypeQueryParamValues.Legacy}&per_page=100`)
      .pipe(
        map((response: PaginatedResource<ReportLayout<any>>) => {
          const reportLayouts: ReportLayout<any>[] = response.results || [];
          this._reportLayouts.next(reportLayouts);
          return reportLayouts;
        }),
      );
  }

  /**
   * Get contact by id
   */
  getReportLayoutById(id: number): Observable<ReportLayout<any>> {
    return this._httpClient.get<any>(`report_layouts/${id}`).pipe(
      map((response) => {
        this._reportLayout.next(response.report_layout);
        return response.report_layout;
      }),
    );
  }

  /**
   * Create contact
   */
  createReport(reportLayout: ReportLayout<any>): Observable<ReportLayout<any>> {
    return this.reportLayouts$.pipe(
      take(1),
      switchMap((reportLayouts) =>
        this._httpClient.post<any>(`report_layouts`, { report_layout: reportLayout }).pipe(
          map((reponse) => {
            // Update the contacts with the new contact
            this._reportLayouts.next([reponse.report_layout, ...reportLayouts]);

            // Return the new contact
            return reponse.report_layout;
          }),
        ),
      ),
    );
  }

  uploadCompanyLogo(file: File, reportLayoutId: number): Observable<any | HttpErrorResponse> {
    const uploadData = new FormData();
    uploadData.append('image[attachment]', file);
    const URL = `report_layouts/${reportLayoutId}/logo`;

    return this._httpClient.post<any>(URL, uploadData).pipe(
      map((response) => response),
      catchError((err) => of(err)),
    );
  }

  deleteCompanyLogo(reportLayoutId: number): Observable<any | HttpErrorResponse> {
    const URL = `report_layouts/${reportLayoutId}/logo`;

    return this._httpClient.delete<any>(URL).pipe(
      map((response) => response),
      catchError((err) => of(err)),
    );
  }

  updateReport(reportLayout: ReportLayout<any>): Observable<ReportLayout<any>> {
    return this.reportLayouts$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .patch<any>(`report_layouts/${reportLayout.id}`, {
            report_layout: reportLayout,
          })
          .pipe(
            map((response) => {
              this._reportLayout.next(response.report_layout);

              // Return the updated contact
              return response.report_layout;
            }),
          ),
      ),
    );
  }

  duplicateReport(reportLayout: ReportLayout<any>): Observable<ReportLayout<any>> {
    return this._httpClient
      .put<any>(`report_layouts/${reportLayout.id}/duplicate`, {
        report_layout: reportLayout,
      })
      .pipe(
        map((response) => {
          // Return the deleted status
          return response.report_layout;
        }),
      );
  }

  deleteReport(id: number): Observable<any> {
    return this._httpClient.delete<any>(`report_layouts/${id}`).pipe(
      map((response) => {
        // Return the deleted status
        return response.report_layout;
      }),
    );
  }

  runReport(
    id: number,
    fields: string[] = [],
    queryFilters: any = {},
    page: number = 0,
    size: number = 20,
    sort = 'projects.created_at',
    order: 'asc' | 'desc' | '' = 'desc',
    format: string = 'pdf',
    addChildren: boolean = false,
    mSort?: { field: string; order: string }[],
    hasNestedFiltersOn: boolean = false,
  ): Observable<any> {
    let node = this._massageRequestFields(clone(fields));

    let filter = clone(queryFilters);
    for (let key in filter) {
      if (filter[key] && filter[key].length > 0) {
        if (Array.isArray(filter[key])) {
          filter[key] = filter[key].join(',');
        }
      }
    }

    let _connection = hasNestedFiltersOn ? GraphqlConnection.projectsSqlBased : GraphqlConnection.projects;

    const query = {
      query: {
        [_connection]: {
          __args: {
            filter,
            page: page + 1,
            perPage: size,
          },
          pageInfo: {
            hasNextPage: true,
            hasPreviousPage: true,
            endCursor: true,
            startCursor: true,
            totalCount: true,
            filteredCount: true,
          },
          edges: {
            cursor: true,
            node,
          },
        },
      },
    };
    if (mSort.length) {
      query.query[_connection].__args['msort'] = mSort;
    } else {
      query.query[_connection].__args['sort'] = { field: sort, order };
    }
    const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

    return this._httpClient
      .post<any>(`graphql?format=${format}&report_layout_id=${id}${addChildren ? '&force_include_child=true' : ''}`, {
        query: gqlQuery,
      })
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  downloadPostalReport(): Observable<any> {
    return this._httpClient.get<any>(`reports/postal_acquisition`).pipe(
      map((response) => {
        return response.data;
      }),
    );
  }

  private _massageRequestFields(fields: string[]) {
    let requestFields = {};
    for (let field of fields) {
      requestFields[field] = true;
    }
    if (!fields.includes('id')) {
      requestFields['id'] = true;
    }
    if (!fields.includes('address_string')) {
      requestFields['address_string'] = true;
    }
    if (!fields.includes('featured_image_url')) {
      requestFields['featured_image_url'] = true;
    }
    if (!fields.includes('title')) {
      requestFields['title'] = true;
    }
    if (!fields.includes('address_string')) {
      requestFields['address_string'] = true;
    }

    if (fields.includes('address')) {
      requestFields['address'] = {
        latitude: true,
        longitude: true,
      };
    }

    requestFields['project_stage'] = {
      id: true,
      name: true,
    };
    requestFields['investment_type'] = {
      id: true,
      name: true,
    };
    requestFields['project_type'] = {
      id: true,
      name: true,
    };
    requestFields['property_type'] = {
      id: true,
      name: true,
    };
    requestFields['vehicle'] = {
      id: true,
      name: true,
    };
    requestFields['team'] = {
      id: true,
      name: true,
    };
    return requestFields;
  }
}
